import {
  IDailyRevenuePaymentsData,
  IDailyRevenuePaymentsResult,
  useDailyRevenuePaymentsAPI,
} from "@/app/hooks/api_hooks/payment/useDailyRevenuePaymentsAPI";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BsCurrencyRupee } from "react-icons/bs";
import { useRouter } from "@/app/hooks/useRouter";
import { useLoader } from "@/app/hooks/useLoader";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import PaginationComponent from "@/app/components/ui/Pagination";

const PAGE_SIZE = 30;

const DailyRevenuePaymentsComponent = () => {
  const { NO_CART } = useImageConstants();
  const isLoading = useLoader("isFinanceSummaryLoading");
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const { execute: fetchDailyRevenuePayments } = useDailyRevenuePaymentsAPI();
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const today = new Date();
  const [selectionDate, setSelectionDate] = useState({
    startDate: today,
    endDate: today,
    key: "selectionDate",
  });
  const [dailyRevenuePaymentsData, setDailyRevenuePaymentsData] = useState<IDailyRevenuePaymentsData | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const getStartOfDayEpoch = (date: Date) =>
    Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).getTime() / 1000);

  const getEndOfRangeEpoch = (date: Date) =>
    Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0, 0).getTime() / 1000);

  const getFormattedCalendarDate = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const apiStartDate = getStartOfDayEpoch(new Date(selectionDate.startDate));
  const apiEndDate = getEndOfRangeEpoch(new Date(selectionDate.endDate));
  const calendarStartDate = getFormattedCalendarDate(new Date(selectionDate.startDate));
  const calendarEndDate = getFormattedCalendarDate(new Date(selectionDate.endDate));

  const loadDailyRevenuePayments = (page: number) => {
    if (!mappedClinicId) {
      setDailyRevenuePaymentsData(null);
      return;
    }
    fetchDailyRevenuePayments({
      clinic_id: mappedClinicId,
      start_date: apiStartDate,
      end_date: apiEndDate,
      page_number: page,
      page_size: PAGE_SIZE,
    }).then(({ status, message }) => {
      if (status && message) {
        setDailyRevenuePaymentsData(message);
      } else {
        setDailyRevenuePaymentsData({
          total_payment: 0,
          total_revenue: 0,
          total_wallet_transactions: 0,
          total_delta: 0,
          total_overdue: 0,
          total_records: 0,
          page_number: page,
          page_size: PAGE_SIZE,
          results: [],
        });
      }
    });
  };

  const onApply = () => {
    setCurrentPage(1);
    loadDailyRevenuePayments(1);
  };

  useEffect(() => {
    if (mappedClinicId) {
      loadDailyRevenuePayments(currentPage);
    }
  }, [mappedClinicId, currentPage]);

  const rows = dailyRevenuePaymentsData?.results ?? [];
  const totalItems = dailyRevenuePaymentsData?.total_records ?? 0;
  const formatAmount = (amount?: number) => Number(amount || 0).toFixed(2);

  const onCustomerClick = (entry: IDailyRevenuePaymentsResult) => {
    const userId = entry?.user_id;
    const petId =
      entry?.pet_id ??
      (entry as IDailyRevenuePaymentsResult & { petId?: string | null; pet_details?: { pet_id?: string | null } })
        ?.petId ??
      (entry as IDailyRevenuePaymentsResult & { pet_details?: { pet_id?: string | null } })?.pet_details?.pet_id ??
      "";
    if (!userId) return;
    const encodedUserId = encodeURIComponent(userId);
    const baseAppointmentsRoute = getRouteKey("APPOINTMENTS_TAB", "url");
    const destination = petId
      ? `${baseAppointmentsRoute}/${encodedUserId}/${encodeURIComponent(petId)}`
      : `${baseAppointmentsRoute}/${encodedUserId}`;
    navigate(destination);
  };

  return (
    <div className="h-full">
      <div className="pt-3 pb-0.5 rounded-lg bg-white px-2">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b pb-2 gap-2">
          <p className="text-sm font-semibold mt-2">Daily Revenue &amp; Payments</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <OMCalendarComponent
              selectionDate={selectionDate}
              setSelectionDate={setSelectionDate}
              startDate={calendarStartDate}
              endDate={calendarEndDate}
              handelClick={() => {
                // no-op
              }}
            />
            <button
              className="bg-black text-xs text-white font-medium rounded-md px-3 h-9 w-full sm:w-24 shrink-0"
              type="button"
              onClick={onApply}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
      {isLoading ? (
        <TableSkeleton />
      ) : rows.length > 0 ? (
        <div className="pb-2 rounded-lg bg-white px-2">
          <div className="mt-2 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto">
            <div className="w-full">
              <div className="inline-block min-w-full align-middle">
                <table className="w-full min-w-[840px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                  <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                    <tr>
                      {tableHeader.map((key, index) => (
                        <th
                          key={index}
                          scope="col"
                          className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black"
                        >
                          {key.item}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((entry: IDailyRevenuePaymentsResult, index) => {
                      const isFirst = index === 0;
                      const isLast = index === rows.length - 1;
                      const isSingle = rows.length === 1;
                      return (
                        <tr
                          key={entry.user_id}
                          className={`
                          bg-white
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                        >
                          <td
                            className={`
                            whitespace-nowrap px-2 py-3 text-xs text-black
                            ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                          >
                            <button
                              type="button"
                              className="text-left text-blue-600 hover:text-blue-700 hover:underline"
                              onClick={() => onCustomerClick(entry)}
                            >
                              {entry.user_name || "-"}
                            </button>
                          </td>
                          <td
                            className={`whitespace-nowrap px-2 py-3 text-xs ${entry.is_booking_present ? "text-[#079F60]" : "text-[#FF0303]"}`}
                          >
                            {entry.is_booking_present ? "Yes" : "No"}
                          </td>
                          <td className="whitespace-nowrap px-2 py-3 text-xs text-[#079F60]">
                            <span className="inline-flex items-center">
                              <BsCurrencyRupee className="w-3 h-3" />
                              {formatAmount(entry.invoiced_value)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-2 py-3 text-xs text-[#079F60]">
                            <span className="inline-flex items-center">
                              <BsCurrencyRupee className="w-3 h-3" />
                              {formatAmount(entry.payment_done)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-2 py-3 text-xs text-[#079F60]">
                            <span className="inline-flex items-center">
                              <BsCurrencyRupee className="w-3 h-3" />
                              {formatAmount(entry.wallet_transactions)}
                            </span>
                          </td>
                          <td
                            className={`whitespace-nowrap px-2 py-3 text-xs ${entry.delta > 0 ? "text-[#FF0303]" : "text-[#079F60]"}`}
                          >
                            <span className="inline-flex items-center">
                              <BsCurrencyRupee className="w-3 h-3" />
                              {formatAmount(entry.delta)}
                            </span>
                          </td>
                          <td
                            className={`
                            whitespace-nowrap px-2 py-3 text-xs ${entry.overdue >= 0 ? "text-[#079F60]" : "text-[#FF0303]"}
                            ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                          >
                            <span className="inline-flex items-center">
                              <BsCurrencyRupee className="w-3 h-3" />
                              {formatAmount(entry.overdue)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="text-xs bg-[#F5F5F5] font-medium">
                      <td className="sticky bottom-0 z-20 text-left px-2 py-3 bg-[#F5F5F5] border-t rounded-bl-2xl"></td>
                      <td className="sticky bottom-0 z-20 text-left px-2 py-3 bg-[#F5F5F5] border-t">Total Value:</td>
                      <td className="sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t text-[#079F60]">
                        <span className="inline-flex items-center">
                          <BsCurrencyRupee className="w-3 h-3" />
                          {formatAmount(dailyRevenuePaymentsData?.total_revenue)}
                        </span>
                      </td>
                      <td className="sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t text-[#079F60]">
                        <span className="inline-flex items-center">
                          <BsCurrencyRupee className="w-3 h-3" />
                          {formatAmount(dailyRevenuePaymentsData?.total_payment)}
                        </span>
                      </td>
                      <td className="sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t text-[#079F60]">
                        <span className="inline-flex items-center">
                          <BsCurrencyRupee className="w-3 h-3" />
                          {formatAmount(dailyRevenuePaymentsData?.total_wallet_transactions)}
                        </span>
                      </td>
                      <td
                        className={`sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t ${
                          (dailyRevenuePaymentsData?.total_delta ?? 0) > 0 ? "text-[#FF0303]" : "text-[#079F60]"
                        }`}
                      >
                        <span className="inline-flex items-center">
                          <BsCurrencyRupee className="w-3 h-3" />
                          {formatAmount(dailyRevenuePaymentsData?.total_delta)}
                        </span>
                      </td>
                      <td
                        className={`sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t rounded-br-2xl ${
                          (dailyRevenuePaymentsData?.total_overdue ?? 0) >= 0 ? "text-[#079F60]" : "text-[#FF0303]"
                        }`}
                      >
                        <span className="inline-flex items-center">
                          <BsCurrencyRupee className="w-3 h-3" />
                          {formatAmount(dailyRevenuePaymentsData?.total_overdue)}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {totalItems > PAGE_SIZE ? (
            <div className="flex justify-end pt-2">
              <PaginationComponent
                className="pagination-bar"
                currentPage={currentPage}
                pageSize={PAGE_SIZE}
                totalCount={totalItems}
                onPageChange={(pageNumber) => setCurrentPage(pageNumber)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {!isLoading && rows.length === 0 ? (
        <div className="bg-white h-[400px] mt-3">
          <NoItemFoundComponent
            message=""
            description="No Daily Revenue & Payments Found"
            imageURL={NO_CART}
            InputClassName="my-0 h-full"
          />
        </div>
      ) : null}
    </div>
  );
};

export default DailyRevenuePaymentsComponent;

const tableHeader = [
  { item: "Customer Name" },
  { item: "Booking Present" },
  { item: "Invoiced Value" },
  { item: "Payment Done" },
  { item: "Wallet Transactions" },
  { item: "Delta" },
  { item: "Overdue" },
];

const TableSkeleton = () => {
  return (
    <div className="mt-3 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] animate-pulse">
      <div className="w-full overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <table className="w-full min-w-[840px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
            <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-10">
              <tr>
                {[...Array(7)].map((_, index) => (
                  <th key={index} className="p-2 py-3 text-left text-xs font-medium text-gray-300 tracking-wider">
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(8)].map((_, rowIndex) => (
                <tr key={rowIndex} className="bg-white">
                  {[...Array(7)].map((_, colIndex) => (
                    <td key={colIndex} className="px-2 py-3 whitespace-nowrap text-xs text-gray-300">
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
